import {Component, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {Asset, ASSET_STATUS_CONTRACT_READY, AssetInfo, assetMessage, assetMoveErrorCodes, AssetSaleInfo} from '../../shared/domain/item';
import {AssetPackageDto} from '../../shared/domain/asset-package';
import {DialogService, UnsavedChangeDetector} from '@rbaip/ui-component';
import {ItemSummaryItemSummaryService} from '../../shared/asset/item-summary-item-summary.service';
import {Subscription} from 'rxjs';
import {AssetSaleInfoComponent} from "../asset-sale-info/asset-sale-info.component";

@Component({
  selector: 'lib-item-summary-item-sale-info-section',
  templateUrl: './item-sale-info-section.component.html'
})
export class ItemSaleInfoSectionComponent extends UnsavedChangeDetector implements OnDestroy {
  @Input() asset: Asset;
  @Input() assetInfo: AssetInfo;
  @Input() assetSaleInfo: AssetSaleInfo;
  @Input() currentDefaultSaleInfo: AssetSaleInfo;
  @Input() assetControlGroup: AssetPackageDto;
  @Input() editEnabled: boolean;
  @Input() isVSOEvent: boolean;
  @Input() onlyShowOpportunityInfo: boolean;
  @Input() itemSummaryService: ItemSummaryItemSummaryService;
  @Output() assetChanged = new EventEmitter<Asset>();
  @Output() editModeChanged = new EventEmitter<boolean>();

  @ViewChild('asseSaleInfoComponent') asseSaleInfoComponent: AssetSaleInfoComponent;

  private saveAssetSaleInformationSubscription: Subscription;

  constructor(private dialogService: DialogService) {
    super();
  }

  ngOnDestroy(): void {
    if (this.saveAssetSaleInformationSubscription) {
      this.saveAssetSaleInformationSubscription.unsubscribe();
    }
  }

  editAssetSaleInfo() {
    if ( this.assetSaleInfo != null ) {
      this.toggleEditMode();
      if ( this.assetSaleInfo.isEditable ) {
        this.assetSaleInfo.highlightRequiredField = true;
      }
    }
  }

  cancelSaleInfo() {
    this.toggleEditMode();
    this.assetChanged.emit(null);
  }

  saveAssetSaleInfo() {
    if (this.assetSaleInfo) {
      if (this.assetSaleInfo.isAuctionSite && !this.assetSaleInfo.siteId) {
        this.dialogService.showMessage('Selling Location is required ', 5000);
        return;
      }
      if (!this.assetSaleInfo.isAuctionSite && !this.assetSaleInfo.addNewAddress && !this.assetSaleInfo.locationId) {
        this.dialogService.showMessage('Selling Location is required ', 5000);
        return;
      }
      if (this.assetSaleInfo.addNewAddress && this.assetSaleInfo.newLocation && !this.assetSaleInfo.newLocation.locationDesc) {
        this.dialogService.showMessage('Selling Location Name is Required ', 5000);
        return;
      }
      if (this.assetSaleInfo.addNewAddress && this.assetSaleInfo.newLocation && !this.asseSaleInfoComponent.isValidAddress()) {
        this.dialogService.showMessage('Please enter valid Selling Location Address ', 5000);
        return;
      }
      if (this.assetSaleInfo.addNewAppraisalAddress && this.assetSaleInfo.newAppraisalLocation && !this.assetSaleInfo.newAppraisalLocation.locationDesc) {
        this.dialogService.showMessage('Asset Origin Location Name is Required ', 5000);
        return;
      }
      if (this.assetSaleInfo.addNewAppraisalAddress && this.assetSaleInfo.newAppraisalLocation && !this.asseSaleInfoComponent.isValidAppraisalAddress()) {
        this.dialogService.showMessage('Please enter valid Asset Origin Address ', 5000);
        return;
      }
      if (this.assetInfo.assetStatusId >= ASSET_STATUS_CONTRACT_READY && !this.assetSaleInfo.isPostContract && !this.assetSaleInfo.isValidAfterContractReady(this.isVSOEvent)) {
        let errorMsg = '';
        if (this.assetInfo.assetStatusId === ASSET_STATUS_CONTRACT_READY) {
          errorMsg = 'It is not possible to remove required fields from an asset in Contract Ready. Please move the asset back to Work In Progress in order to make these changes';
        } else if (this.assetInfo.assetStatusId > ASSET_STATUS_CONTRACT_READY) {
          errorMsg = 'It is not possible to remove required fields from operationalized assets. You can only replace the current values';
        }

        this.assetSaleInfo.highlightRequiredField = true;
        this.dialogService.showMessage(errorMsg, 5000);
        return;
      }
    }

    const newAssetOrigin = this.assetSaleInfo.getAssetOrigin();

    if (Asset.isPendingAppraisal(this.asset) && !AssetSaleInfo.checkLocationsAreSame(this.asset.getAssetOrigin(), newAssetOrigin)) {
      let confirmAssetPendingAppraisal = assetMessage.confirmAssetPendingAppraisal;
      this.dialogService.showMessageDialog(confirmAssetPendingAppraisal)
        .afterClosed().subscribe(result => {
        if ( result === 'OK' ) {
          this.submitAssetDetails(this.asset);
        }
      });
    } else {
      this.submitAssetDetails(this.asset);
    }
  }

  submitAssetDetails(assetDto: Asset){
    const asset1 = this.assetSaleInfo.setAssetWithSaleInfo(assetDto);
    this.dialogService.showProgress();
    this.saveAssetSaleInformationSubscription = this.itemSummaryService.updateSaleInformation(asset1).subscribe(response => {
      if (response && response.error) {
        this.dialogService.hideProgress();
        let errorMessage;
        if (response.message && response.message.indexOf(assetMoveErrorCodes.invalidOperationalEntity) >= 0) {
          errorMessage = assetMessage.assetMoveOperationalEntityError;
        } else {
          errorMessage = assetMessage.assetMoveError;
          errorMessage.message = errorMessage.message.replace(':message', 'Unexpected error occurred while trying to update asset sale information. Please contact tech support. ');
        }
        this.dialogService.showMessageDialog(errorMessage);
      } else {
        this.dialogService.showMessage('Successfully saved Asset Information !');
        this.dialogService.hideProgress();
        this.asset = Asset.constructFromObjectJson(response);
        this.assetChanged.emit(this.asset);
        this.toggleEditMode();
      }
    });
  }

  hasChanges(): boolean {
    return this.assetSaleInfo.isEditable;
  }

  enableSaveLocationOnModelSave(): boolean {
    return !!this.asset.assetId && !!this.assetSaleInfo.locationId ;
  }

  private toggleEditMode(): void {
    this.assetSaleInfo.isEditable = !this.assetSaleInfo.isEditable;
    this.broadcastEditModeChanged(this.assetSaleInfo.isEditable);
  }

  private broadcastEditModeChanged(isOnEditMode: boolean): void {
    this.editModeChanged.emit(isOnEditMode);
  }
}
